import { useCallback, useEffect, useState } from "react";
import {
  apiCreateOptionStrategy,
  apiDeleteOptionStrategy,
  apiGetOptionStrategies,
} from "api/apiOptionStrategies";

const makeKey = (symbol, modelId) => `${symbol}-${modelId}`;

// "Track signals" from Options Analysis / Options Leaderboard: a signals-only
// options strategy per symbol + model (daily 15:50 signal and the email, no
// trades). Keeps a local map so any screen can show and toggle the state.
// A symbol + model that already has a trading options strategy counts as
// tracked too (it gets the same signal). onChanged runs after a toggle.
const useTrackSignals = ({ onChanged } = {}) => {
  const [trackedMap, setTrackedMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchTracked = useCallback(async () => {
    setIsLoading(true);
    try {
      const strategies = await apiGetOptionStrategies();
      const map = {};
      strategies
        .filter((s) => s.models?.length === 1)
        .forEach((s) => {
          map[makeKey(s.symbol, s.models[0]._id)] = s;
        });
      setTrackedMap(map);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTracked();
  }, [fetchTracked]);

  const isTracked = useCallback(
    (symbol, modelId) => !!trackedMap[makeKey(symbol, modelId)],
    [trackedMap],
  );

  // analysisParams: { targetGainPercent, minDays, maxDays, excludedEntryWeekdays }
  // Returns true when tracking started, false when it stopped.
  const toggleTracking = useCallback(
    async (symbol, modelId, startDate, analysisParams = {}) => {
      const key = makeKey(symbol, modelId);
      const existing = trackedMap[key];

      if (existing) {
        if (!existing.signalsOnly) {
          throw new Error(
            `${symbol} has a trading options strategy -- manage it in Options Strategies`,
          );
        }
        // deleting a strategy deletes its trades: the backend refuses if it
        // has any (this map may be stale)
        await apiDeleteOptionStrategy(existing._id, { onlyIfNoTrades: true });
        setTrackedMap((prevState) => {
          const nextState = { ...prevState };
          delete nextState[key];
          return nextState;
        });
        onChanged?.();
        return false;
      }

      const minDays = analysisParams.minDays ?? 1;
      const strategy = await apiCreateOptionStrategy({
        name: `${symbol} signals`,
        symbol,
        models: [modelId],
        signalsOnly: true,
        backtestStartDate: startDate,
        params: {
          takeProfitPercent: analysisParams.targetGainPercent,
          minDays,
          // the analysis' hold window is the natural expiry window for alerts
          maxDteBars: Math.max(analysisParams.maxDays ?? minDays, minDays),
          excludedEntryWeekdays: analysisParams.excludedEntryWeekdays ?? [],
        },
      });
      setTrackedMap((prevState) => ({
        ...prevState,
        [key]: { ...strategy, models: [{ _id: modelId }] },
      }));
      onChanged?.();
      return true;
    },
    [trackedMap, onChanged],
  );

  return { isLoading, isTracked, toggleTracking, refetch: fetchTracked };
};

export default useTrackSignals;
